<template>
	<Floater placement="bottom-end" :offsetDistance="8" :arrow="false">
		<button
			class="btn btn-ghost btn-sm relative"
			:class="{ 'text-primary': activeUploads > 0 }"
			:aria-label="t('files.uploadDropdown.title')">
			<UploadCloud />
			<span v-if="activeUploads" class="status-dot" />
		</button>
		<template #content="{ close }">
			<div class="upload-dropdown">
				<div class="upload-dropdown__header">
					<div>
						<p class="upload-dropdown__title">{{ t('files.uploadDropdown.title') }}</p>
						<p class="upload-dropdown__subtitle">{{ t('files.uploadDropdown.subtitle') }}</p>
					</div>
					<button class="btn btn-ghost btn-xs" @click="close">
						{{ t('files.uploadDropdown.close') }}
					</button>
				</div>

				<div v-if="!recentUploads.length" class="upload-dropdown__empty">
					<p>{{ t('files.uploadDropdown.emptyTitle') }}</p>
					<p class="text-base-content/70 text-xs">{{ t('files.uploadDropdown.emptySubtitle') }}</p>
				</div>

				<ul v-else class="upload-dropdown__list">
					<li v-for="upload in recentUploads" :key="upload.id" class="upload-dropdown__item">
						<CheckCircle2 v-if="upload.status === 'success'" class="status-icon text-success" />
						<AlertCircle v-else-if="upload.status === 'error'" class="status-icon text-error" />
						<Loader2
							v-else-if="upload.status === 'uploading'"
							class="status-icon text-primary animate-spin-slow" />
						<UploadCloud v-else class="status-icon text-base-content/60" />
						<div class="upload-dropdown__details">
							<div class="upload-dropdown__name">{{ upload.name }}</div>
							<div class="upload-dropdown__meta">
								{{ filesStore.makHumanFileSize(upload.size) }}
								<!-- <span v-if="upload.target" class="upload-dropdown__target">• {{ upload.target }}</span> -->
							</div>
						</div>
						<span class="upload-dropdown__status text-success" v-if="upload.status === 'success'">
							{{ t('files.uploadDropdown.statusDone') }}
						</span>
						<span class="upload-dropdown__status text-error" v-else-if="upload.status === 'error'">
							{{ t('files.uploadDropdown.statusError') }}
						</span>
						<span class="upload-dropdown__status text-primary" v-else-if="upload.status === 'uploading'">
							{{ t('files.uploadDropdown.statusUploading') }}
						</span>
						<span class="upload-dropdown__status text-base-content/60" v-else>
							{{ t('files.uploadDropdown.statusQueued') }}
						</span>
					</li>
				</ul>
			</div>
		</template>
	</Floater>
</template>

<script setup lang="ts">
	import { computed } from 'vue'
	import { storeToRefs } from 'pinia'
	import { useFilesStore } from '@/stores/files.store'
	import Floater from '@/components/common/Floater.vue'
	import { AlertCircle, CheckCircle2, Loader2, UploadCloud } from 'lucide-vue-next'
	import { useI18n } from 'vue-i18n'
	import { _translations } from './_translations'

	const filesStore = useFilesStore()
	const { uploads } = storeToRefs(filesStore)

	const recentUploads = computed(() => uploads.value.slice(0, 8))
	const activeUploads = computed(() => uploads.value.filter((upload) => upload.status === 'uploading').length)

	const { t } = useI18n(_translations)
</script>

<style scoped>
	@reference '@/styles/tailwind.css';
	.upload-dropdown {
		@apply w-80 p-1;
	}
	.upload-dropdown__header {
		@apply mb-3 flex items-start justify-between gap-2;
	}
	.upload-dropdown__title {
		@apply text-base-content text-sm font-semibold;
	}
	.upload-dropdown__subtitle {
		@apply text-base-content/70 text-xs;
	}
	.upload-dropdown__empty {
		@apply bg-base-200/60 text-base-content/80 space-y-1 rounded-lg p-3 text-sm;
	}
	.upload-dropdown__list {
		@apply divide-base-200 divide-y;
	}
	.upload-dropdown__item {
		@apply flex items-start gap-3 py-2;
	}
	.upload-dropdown__details {
		@apply min-w-0 flex-1;
	}
	.upload-dropdown__name {
		@apply text-base-content truncate text-sm font-medium;
	}
	.upload-dropdown__meta {
		@apply text-base-content/70 text-[11px];
	}
	.upload-dropdown__target {
		@apply ml-1 truncate text-[11px];
	}
	.status-icon {
		@apply mt-0.5 flex-shrink-0;
	}
	.upload-dropdown__status {
		@apply text-[11px] font-semibold tracking-wide uppercase;
	}
	.status-dot {
		@apply border-base-100 bg-primary absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border;
	}
	.animate-spin-slow {
		animation: spin 1.1s linear infinite;
	}
	@keyframes spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}
</style>
